import {
  StoreModel,
  StoreModelDate,
  StoreModelJsonType,
  StoreModelType,
} from '@aksia/infrastructure';
import {
  IExtraction,
  IExtractionDataPoint,
  IEmailHeader,
  IEmailContent,
  IEmailExtraction,
  IEmailAttachment,
  IAttachmentContent,
  TExtractionSourceDetails,
  IExtractedData,
  IExtractedIntramonthData,
  IExtractedAumData,
  IExtractedCashflowData,
  IExtractedExposureData,
  IExtractedMtdData,
  IExtractedTermData,
  IExtractedDocumentClassificationData,
  IExtractedResolvedEntity,
  IEmailExtractionUploadResponse,
} from '../../interfaces/ai-extractors/extractors.interface';
import {
  ExtractionDataPointTypeEnum,
  ExtractionDataPointStatusEnum,
  ExtractionSourceTypeEnum,
  ExtractionStatusEnum,
  ExtractionDataPointRecommendationEnum,
  DocumentTypeEnum,
} from '../../enums/extractions.enums';
import {
  PublicReturnSourceEnum,
  PublicReturnFeeEnum,
  PublicReturnGeneralClassificationEnum,
  AUMSourceEnum,
} from '../../enums/periodicStream.enum';
import { CurrencyStringEnum } from '../../enums/entity.enum';

//#region Common Extractor Classes

export class ExtractionDataPoint<V extends ExtractedData>
  extends StoreModel
  implements IExtractionDataPoint<V>
{
  id: string | undefined = undefined;
  extractionRequestId: string | undefined = undefined;
  dataPointType: ExtractionDataPointTypeEnum | undefined = undefined;
  aiExtractedData: V | undefined = undefined;
  enrichedData: V | undefined = undefined;
  userUpdatedData: V | undefined = undefined;
  status: ExtractionDataPointStatusEnum | undefined = undefined;
  reason: string | null | undefined = undefined;
  createdBy: string | undefined = undefined;
  updatedBy: string | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  createdOnUtc: Date | string | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  updatedOnUtc: Date | string | undefined = undefined;

  extractionRequest: IExtraction | undefined = undefined;

  get dataPoint(): V | undefined {
    return this.userUpdatedData || this.enrichedData || this.aiExtractedData;
  }

  /**
   * TODO (Refactor)
   * This transformation can be automated with the StoreModelType.
   * Currently, we need to manually call $onTransform to transform the data into the correct class instances.
   * This is because we need to determine the correct class based on the dataPointType, which is not possible with the current StoreModelType implementation.
   * We might need to enhance the StoreModelType to support conditional types or add a new decorator to handle this specific case.
   * For now, we will keep this transformation logic here, but it should be refactored in the future to leverage the StoreModelType system.
   */
  override $onTransform = () => {
    ExtractionDataPoint.transform(this);
  };

  static transform = (self: ExtractionDataPoint<ExtractedData>) => {
    self.aiExtractedData = self.aiExtractedData
      ? (JSON.parse(self.aiExtractedData as unknown as string) as ExtractedData)
      : undefined;

    self.enrichedData = self.enrichedData
      ? (JSON.parse(self.enrichedData as unknown as string) as ExtractedData)
      : undefined;

    self.userUpdatedData = self.userUpdatedData
      ? (JSON.parse(self.userUpdatedData as unknown as string) as ExtractedData)
      : undefined;

    const PointClass = ExtractedData.transform(self.dataPointType!);

    if (self.userUpdatedData) {
      const point = new PointClass();
      self.userUpdatedData = Object.assign(point, self.userUpdatedData);
      self.userUpdatedData.$onTransform?.();
    }
    if (self.enrichedData) {
      const point = new PointClass();
      self.enrichedData = Object.assign(point, self.enrichedData);
      self.enrichedData.$onTransform?.();
    }
    if (self.aiExtractedData) {
      const point = new PointClass();
      self.aiExtractedData = Object.assign(point, self.aiExtractedData);
      self.aiExtractedData.entityO2Id = self.aiExtractedData.entityO2Id ?? -1;
      self.aiExtractedData.$onTransform?.();
    }
  };
}

export class Extraction<T, U, V extends ExtractedData>
  extends StoreModel
  implements IExtraction<T, U, V>
{
  id: string | undefined = undefined;
  sourceType: ExtractionSourceTypeEnum | undefined = undefined;
  sourceReferenceId: string | undefined = undefined;
  status: ExtractionStatusEnum | undefined = undefined;
  reason: string | null | undefined = undefined;
  assignedTo: string | undefined = undefined;
  errorMessage: string | undefined = undefined;
  sourceTitle: string | undefined = undefined;
  sourceDescription: T | undefined = undefined;
  sourceDetails: U | undefined = undefined;
  tags: string[] | undefined = undefined;
  dataPointTypes: ExtractionDataPointTypeEnum[] | undefined = undefined;
  documentTypes: DocumentTypeEnum[] | undefined = undefined;
  resultStatusCounts: Record<string, number> | undefined = undefined;
  createdBy: string | undefined = undefined;
  updatedBy: string | undefined = undefined;
  requestedOnUtc: Date | string | undefined = undefined;
  updatedOnUtc: Date | string | undefined = undefined;
  completedOnUtc: Date | string | undefined = undefined;
  results: Array<ExtractionDataPoint<V>> | undefined = undefined;
  fileUploads: Array<IEmailExtractionUploadResponse> | undefined = undefined;
}

export class ExtractedData extends StoreModel implements IExtractedData {
  entityO2Id?: number;
  entityName!: string;
  entityType?: number;
  assetClass?: string;
  relatedEntities?: Array<{
    entityO2Id: number;
    entityName: string;
    entityType: number;
  }>;
  shareClassName?: string;
  recommendationValue?: ExtractionDataPointRecommendationEnum;
  recommendationHoverText?: string;
  addedBy?: string | null;
  byShareClass?: Map<string, ExtractionDataPoint<ExtractedData>>;

  static transform = (dataPointType: ExtractionDataPointTypeEnum) => {
    let classPoint;

    switch (dataPointType) {
      case ExtractionDataPointTypeEnum.Intramonth:
        classPoint = ExtractedIntramonthData;
        break;
      case ExtractionDataPointTypeEnum.Mtd:
        classPoint = ExtractedMtdData;
        break;
      case ExtractionDataPointTypeEnum.Aum:
        classPoint = ExtractedAumData;
        break;
      case ExtractionDataPointTypeEnum.Term:
        classPoint = ExtractedTermData;
        break;
      case ExtractionDataPointTypeEnum.CashFlow:
        classPoint = ExtractedCashflowData;
        break;
      case ExtractionDataPointTypeEnum.Exposure:
        classPoint = ExtractedExposureData;
        break;
      case ExtractionDataPointTypeEnum.DocumentClassification:
        classPoint = ExtractedDocumentClassificationData;
        break;
      default:
        classPoint = ExtractedData;
    }

    return classPoint;
  };
}

//#endregion

//#region Email Extractor Classes

export class EmailHeader extends StoreModel implements IEmailHeader {
  from!: string;
  to!: string;
  receivedOnUtc!: Date | string;
  attachments?: number;
  attachmentName?: string;
}

export class EmailAttachment extends StoreModel implements IEmailAttachment {
  attachmentId!: string;
  filename!: string;
  url!: string;
  isEncrypted!: boolean;
  isPasswordProtected!: boolean;
  pageCount?: number | null;
}

export class AttachmentContent
  extends StoreModel
  implements IAttachmentContent
{
  type!: 'attachment';
  url!: string;
  filename!: string;
  isEncrypted!: boolean;
  isPasswordProtected!: boolean;
  pageCount!: number | null;
}

export class EmailContent extends StoreModel implements IEmailContent {
  type!: string;
  bodyUrl?: string;
  url?: string;
  filename?: string;

  @StoreModelType(() => EmailAttachment, 'EmailContent')
  attachments: Array<EmailAttachment> | undefined;
}

//#endregion

//#region Extracted Data Classes

export class ExtractedIntramonthData
  extends ExtractedData
  implements IExtractedIntramonthData
{
  amount: number | undefined = undefined;
  asOf: Date | string | undefined = undefined;
  source: PublicReturnSourceEnum | undefined = undefined;
  feeType: PublicReturnFeeEnum | undefined = undefined;
  classification: PublicReturnGeneralClassificationEnum | undefined = undefined;
  currency: CurrencyStringEnum | undefined = undefined;
  reportingNotesDisplayText?: string;
  reportingNotesHoverText?: string;

  override $onTransform = () => {
    if (this.asOf && typeof this.asOf === 'string') {
      this.asOf = new StoreModelDate(this.asOf);
    }
  };
}

export class ExtractedMtdData
  extends ExtractedData
  implements IExtractedMtdData
{
  amount: number | undefined = undefined;
  asOf: Date | string | undefined = undefined;
  source: PublicReturnSourceEnum | undefined = undefined;
  feeType: PublicReturnFeeEnum | undefined = undefined;
  classification: PublicReturnGeneralClassificationEnum | undefined = undefined;
  currency: CurrencyStringEnum | undefined = undefined;
  reportingNotesDisplayText?: string;
  reportingNotesHoverText?: string;

  override $onTransform = () => {
    if (this.asOf && typeof this.asOf === 'string') {
      this.asOf = new StoreModelDate(this.asOf);
    }
  };
}

export class ExtractedAumData
  extends ExtractedData
  implements IExtractedAumData
{
  amount: number | undefined = undefined;
  asOf: Date | string | undefined = undefined;
  source: AUMSourceEnum | undefined = undefined;
  reportingNotesDisplayText?: string;
  reportingNotesHoverText?: string;

  override $onTransform = () => {
    if (this.asOf && typeof this.asOf === 'string') {
      this.asOf = new StoreModelDate(this.asOf);
    }
  };
}

export class ExtractedTermData
  extends ExtractedData
  implements IExtractedTermData
{
  text?: string;
}

export class ExtractedExposureData
  extends ExtractedData
  implements IExtractedExposureData
{
  amount?: number;
  asOf?: Date | string;

  override $onTransform = () => {
    if (this.asOf && typeof this.asOf === 'string') {
      this.asOf = new StoreModelDate(this.asOf);
    }
  };
}

export class ExtractedCashflowData
  extends ExtractedData
  implements IExtractedCashflowData
{
  amount?: number;
  asOf?: Date | string;

  override $onTransform = () => {
    if (this.asOf && typeof this.asOf === 'string') {
      this.asOf = new StoreModelDate(this.asOf);
    }
  };
}

export class ExtractedDocumentClassificationData
  extends ExtractedData
  implements IExtractedDocumentClassificationData
{
  entities?: Array<{
    entityType: number;
    entityO2Id: number;
    entityName: string;
  }>;
  resolvedEntities?: Array<IExtractedResolvedEntity>;
  effectiveDate?: Date | string;
  documentType?: DocumentTypeEnum;
  documentTypeName?: string;
  userOverridenDisplayText?: string | null;
  userOverridenHoverText?: string | null;
  extractedEntityName?: string;
  extractedEntityType?: number;
  attachmentPath?: string;
  isValid?: boolean;

  override $onTransform = () => {
    if (this.effectiveDate && typeof this.effectiveDate === 'string') {
      this.effectiveDate = new StoreModelDate(this.effectiveDate);
    }
  };
}

export class EmailExtraction
  extends Extraction<
    EmailHeader,
    EmailContent | AttachmentContent,
    ExtractedData
  >
  implements IEmailExtraction
{
  static readonly BODY_CONTENT_ID = 'body';

  static isAttachmentSource(
    sourceDetails: TExtractionSourceDetails | undefined,
  ): sourceDetails is IAttachmentContent {
    return sourceDetails?.type === 'attachment';
  }

  static hasBody(extraction: IEmailExtraction | undefined): boolean {
    return (
      !!extraction?.sourceDetails &&
      !EmailExtraction.isAttachmentSource(extraction.sourceDetails)
    );
  }

  static bodyUrl(extraction: IEmailExtraction | undefined): string | undefined {
    const sourceDetails = extraction?.sourceDetails;
    return sourceDetails && !EmailExtraction.isAttachmentSource(sourceDetails)
      ? (sourceDetails.url ?? sourceDetails.bodyUrl)
      : undefined;
  }

  static sourceAttachments(
    extraction: IEmailExtraction | undefined,
  ): Array<IEmailAttachment> {
    if (!extraction?.sourceDetails) return [];
    const sourceDetails = extraction.sourceDetails;
    if (!EmailExtraction.isAttachmentSource(sourceDetails)) {
      return sourceDetails.attachments ?? [];
    }
    return [
      {
        attachmentId: extraction.sourceReferenceId ?? extraction.id ?? '',
        filename: sourceDetails.filename,
        url: sourceDetails.url,
        isEncrypted: sourceDetails.isEncrypted,
        isPasswordProtected: sourceDetails.isPasswordProtected,
        pageCount: sourceDetails.pageCount,
      },
    ];
  }

  @StoreModelJsonType(() => EmailHeader)
  override sourceDescription: EmailHeader | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  override requestedOnUtc: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  override updatedOnUtc: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  override completedOnUtc: Date | undefined = undefined;

  @StoreModelType(() => ExtractionDataPoint)
  override results: Array<ExtractionDataPoint<ExtractedData>> | undefined =
    undefined;
}

//#endregion
